import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { reduxForm, formValueSelector, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import styled from 'styled-components';
import i18n from './i18n';
import GAEvent from './utils/ga';
import {
  Postnummer,
  OmDeg,
  KontaktOss,
  Resultat,
} from './components';

const Wrapper = styled.section`
  padding: 30px;
  max-width: 617px;
  @media (max-width: 400px) {
    padding: 20px;
  }
`;

const Title = styled.h1`
  color: #444;
`;

const selector = formValueSelector('seniorCalculator');

const validate = ({
  boligverdi: boligverdiRaw,
  fellesgjeld,
  borettslagsleilighet,
  laantakerAlder,
}) => {
  const boligverdi = !boligverdiRaw ? 0 : (typeof boligverdiRaw === 'string' ? parseInt(boligverdiRaw.replace(/[^0-9.]/g, ""), 10) : boligverdiRaw)
  const errors = {};
  if (boligverdi < 100000) {
    errors.boligverdi = 'Boligverdi må være mer enn 100 000 kr';
  }
  if (borettslagsleilighet === 'Ja' && (fellesgjeld === '' || !fellesgjeld)) {
    errors.fellesgjeld = 'Oppgi fellesgjeld';
  }
  if (!laantakerAlder || laantakerAlder === 0) {
    errors.laantakerAlder = 'Oppgi alder';
  }
  return errors;
};

const CalculatorComponent = ({
  postnummer,
  beregnetVerdi,
  laantakerAlder,
  medlaantakerAlder,
  borettslagsleilighet,
  boligverdi,
  fellesgjeld,
  callbackSize,
  handleSubmit,
  }) => {
    const [showResults, setShowResult] = useState(false);
    const [postNumberLoaded, setPostNumberLoaded] = useState(false);
    const submit = () => {
      const errors = validate({
        postnummer,
        beregnetVerdi,
        laantakerAlder,
        borettslagsleilighet,
        boligverdi,
        fellesgjeld,
      });
      if (Object.keys(errors).length === 0) {
        setShowResult(true);
        // GA event på beregn
        GAEvent({
          action: 'Beregn',
          data: {
            boligverdi,
          },
        });
      } else {
        handleSubmit(() => { throw new SubmissionError(); });
      }
    }
    useEffect(() => {
      callbackSize();
    });
    useEffect(() => {
      let timer;
      if (postnummer.length === 4) {
        timer = setTimeout(() => {
          setPostNumberLoaded(true);
          if (!beregnetVerdi.isInnenforSPV && beregnetVerdi.areacodeInfo) {
            // Outside SPV area..
            GAEvent({
              action: 'Avslag',
              data: {
                begrunnelse: 'geografi-feil-fylke',
                postnummer,
              },
            });
          } else if (beregnetVerdi.isInnenforSPV && (beregnetVerdi.areacodeInfo && beregnetVerdi.areacodeInfo[2] !== 'Grønn')) {
            // I området, men ikke grønt postnummer..
            GAEvent({
              action: 'Avslag',
              data: {
                begrunnelse: 'geografi',
                postnummer,
              },
            });
          } else if (beregnetVerdi.isInnenforSPV && beregnetVerdi.areacodeInfo) {
            // Postnummer ok..
            GAEvent({
              action: 'Postnummer',
              data: {
                postnummer,
              },
            });
          }
          // GA-track outside area?
        }, 1000);
      } else {
        clearTimeout(timer);
        setPostNumberLoaded(false);
      }
      return () => {
        clearTimeout(timer);
      };
    }, [postnummer]);
    return (
      <Wrapper>
        <Title>
          {i18n(i => i.TITTEL)}
        </Title>
        {showResults && (
          <Resultat
            beregnetVerdi={beregnetVerdi}
            boligverdi={boligverdi}
            borettslagsleilighet={borettslagsleilighet}
            laantakerAlder={laantakerAlder}
            medlaantakerAlder={medlaantakerAlder}
            fellesgjeld={fellesgjeld}
            goBack={() => setShowResult(false)}
          />
        )}
        {!showResults && (
          <form onSubmit={handleSubmit(submit)}>
            <p>
              {i18n(i => i.INGRESS)}
            </p>
            <Postnummer
              beregnetVerdi={beregnetVerdi}
              postnummer={postnummer}
              loading={postnummer.length === 4 && !postNumberLoaded}
            />
            {postNumberLoaded &&
              beregnetVerdi.areacodeInfo &&
              beregnetVerdi.isInnenforSPV &&
              beregnetVerdi.areacodeInfo[2] === 'Grønn' &&
              (
                <OmDeg borettslagsleilighet={borettslagsleilighet} />
              )
            }
            {postNumberLoaded &&
              beregnetVerdi.areacodeInfo &&
              beregnetVerdi.isInnenforSPV &&
              beregnetVerdi.areacodeInfo[2] !== 'Grønn' &&
              (
                <KontaktOss
                  title="Vi kan dessverre ikke tilby deg seniorlån, men det finnes alternativer som for eksempel rammelån"
                  text="Ta kontakt med oss så kan vi hjelpe å finne en god løsning tilpasset ditt behov!"
                />
              )
            }
            {postNumberLoaded &&
              beregnetVerdi.areacodeInfo &&
              !beregnetVerdi.isInnenforSPV &&
              (
                <KontaktOss
                  title="Vi kan dessverre ikke tilby deg seniorlån"
                  noContact
                />
              )
            }
          </form>
        )}
      </Wrapper>
    );
  }

CalculatorComponent.propTypes = {
  postnummer: PropTypes.string.isRequired,
  laantakerAlder: PropTypes.number.isRequired,
  medlaantakerAlder: PropTypes.number,
  borettslagsleilighet: PropTypes.bool.isRequired,
  boligverdi: PropTypes.number.isRequired,
  fellesgjeld: PropTypes.number.isRequired,
  callbackSize: PropTypes.func.isRequired,
};

export default compose(
  connect(
    state => ({
      postnummer: (selector(state, 'postnummer') && selector(state, 'postnummer').length > 4 ? selector(state, 'postnummer').substring(0, 4) : selector(state, 'postnummer')) || '',
      boligverdi: parseInt((selector(state, 'boligverdi') || '').replace(/\s+/g, ''), 10) || 0,
      fellesgjeld: (selector(state, 'borettslagsleilighet') || false) ? parseInt((selector(state, 'fellesgjeld') || '').replace(/\s+/g, ''), 10) || 0 : 0,
      borettslagsleilighet: selector(state, 'borettslagsleilighet') || false,
      laantakerAlder: parseInt(selector(state, 'laantakerAlder'), 10) || 0,
      medlaantakerAlder: parseInt(selector(state, 'medlaantakerAlder'), 10) || 0,
      // eslint-disable-next-line no-undef
      beregnetVerdi: beregn([
        (parseInt((selector(state, 'boligverdi') || '').replace(/\s+/g, ''), 10) || 0), 
        (parseInt((selector(state, 'fellesgjeld') || '').replace(/\s+/g, ''), 10) || 0),
        (parseInt(selector(state, 'laantakerAlder'), 10) || 0),
        (parseInt(selector(state, 'medlaantakerAlder'), 10) || 0),
        ((selector(state, 'postnummer') && selector(state, 'postnummer').length > 4 ? selector(state, 'postnummer').substring(0, 4) : selector(state, 'postnummer')) || ''),
        selector(state, 'borettslagsleilighet') || false])
    }),
  ),
  reduxForm({
    form: 'seniorCalculator',
    validate: (values) => validate(values),
  }),
)(CalculatorComponent);
